// positionsCalculations.js — All math and derived column calculations for the Positions page
// Pure functions with no React dependency — can be used by any component
//
// Takes a raw position (from the backend /api/positions endpoint) plus live tick data,
// and returns a fully computed row with all 30 columns ready for display.
//
// Uses calcIV and formatDate from the shared calculations.js (Market Watch math).

import { calcIV, formatDate, fmtINR, fmtNum, calcStrikeStockRatio } from './calculations';

// ═══════════════════════════════════════════════════════════════
// COMPUTE POSITION ROW — One row of the Positions table
// ═══════════════════════════════════════════════════════════════

/**
 * @param {Object} position - From backend GET /api/positions
 * @param {Object} ticks    - Live tick data, keyed by instrument_token { [token]: { ltp, volume, oi, depth, ... } }
 * @param {Object} margins  - Margin cache, keyed by tradingsymbol { [tsym]: { totalMargin } }
 * @returns {Object} Fully computed row for the table (all 30 columns)
 */
export function computePositionRow(position, ticks, margins) {
  const pos = position;

  // ─── Live data from WebSocket ticks ───
  const optionTick = ticks[pos.instrument_token] || {};
  const ltp = optionTick.ltp || pos.lastPrice || 0;
  const spot = pos.underlyingToken ? (ticks[pos.underlyingToken]?.ltp || 0) : 0;
  const rawVolume = optionTick.volume || 0;
  const rawOI = optionTick.oi || 0;
  const volume = (pos.lotSize > 0) ? Math.round(rawVolume / pos.lotSize) : rawVolume;
  const oi = (pos.lotSize > 0) ? Math.round(rawOI / pos.lotSize) : rawOI;
  const depth = optionTick.depth || null;
  const bestBuy = depth?.buy?.[0] || null;
  const bestSell = depth?.sell?.[0] || null;

  // ─── Days to expiry ───
  const expiryDate = new Date(pos.expiry);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiryDate.setHours(0, 0, 0, 0);
  const daysToExpiry = Math.max(0, Math.ceil((expiryDate - today) / 86400000));

  // ─── Derived: is this a sold or bought position? ───
  const isSold = pos.quantity < 0;
  const absLots = pos.lots || 0;
  const lotSize = pos.lotSize || 0;
  const absQty = Math.abs(pos.quantity || 0);

  // ─── Do we have a real LTP? ───
  // If LTP is 0 (no tick data, markets closed, no cached price), all LTP-dependent
  // columns should show 0 so the table renders "—" instead of misleading numbers.
  // Without this, profit would show = full premium (because remaining = 0), which is wrong.
  const hasLTP = ltp > 0;

  // ─── Col 12: Percentage — LTP / Avg × 100 ───
  // Shows what % of entry price remains. 100% = no change. <100% = premium decayed (good for sellers)
  const avgPrice = pos.averagePrice || 0;
  const percentage = (hasLTP && avgPrice > 0) ? +((ltp / avgPrice) * 100).toFixed(1) : 0;

  // ─── Col 15: Margin — standalone 1-lot margin from cache. N/A for bought options ───
  const marginData = margins[pos.tradingsymbol] || {};
  const oneLotMargin = marginData.totalMargin || 0;
  const margin = isSold ? oneLotMargin : 0; // Only relevant for sold options

  // ─── Col 16: Total Margin — margin × lots. Approximation until we get actual blocked margin ───
  const totalMargin = isSold ? Math.round(oneLotMargin * absLots) : 0;

  // ─── Col 17: Total Premium — Avg × Lots × Lot Size ───
  // Always calculable (uses avgPrice from broker, not live data)
  const totalPremium = Math.round(avgPrice * absLots * lotSize);

  // ─── Col 18: Remaining — LTP × Lots × Lot Size ───
  // Requires real LTP. Without it, show 0 (table renders "—").
  const remaining = hasLTP ? Math.round(ltp * absLots * lotSize) : 0;

  // ─── Col 19: Profit ───
  // Requires real LTP. Without it, we can't know current value.
  const profit = hasLTP
    ? (isSold ? (totalPremium - remaining) : (remaining - totalPremium))
    : 0;

  // ─── Col 20: Returns — Total Premium / Total Margin × 100 ───
  const returns = totalMargin > 0 ? +((totalPremium / totalMargin) * 100).toFixed(2) : 0;

  // ─── Col 21: Returns Left — Remaining / Total Margin × 100 ───
  const returnsLeft = (hasLTP && totalMargin > 0) ? +((remaining / totalMargin) * 100).toFixed(2) : 0;

  // ─── Col 22: Annualized Return — same exponential formula as MW ───
  let annualizedReturn = 0;
  if (daysToExpiry > 0 && returns > 0) {
    annualizedReturn = +((Math.pow(1 + Math.abs(returns / 100), 365 / daysToExpiry) - 1) * 100).toFixed(1);
  }

  // ─── Col 23: Annualized Returns Left ───
  let annualizedReturnsLeft = 0;
  if (daysToExpiry > 0 && returnsLeft > 0 && hasLTP) {
    annualizedReturnsLeft = +((Math.pow(1 + Math.abs(returnsLeft / 100), 365 / daysToExpiry) - 1) * 100).toFixed(1);
  }

  // ─── Col 25: Current IV — Black-Scholes (same as MW) ───
  // Needs both LTP and spot price to calculate
  const currentIV = (hasLTP && spot > 0) ? calcIV(spot, pos.strike, ltp, daysToExpiry, pos.type) : 0;

  // ─── Strike/Stock ratio — % distance of strike from the underlying spot (same as MW) ───
  const strikeStockRatio = calcStrikeStockRatio(pos.strike, spot);

  // ─── Result date — recompute "is it still upcoming?" vs today (don't trust a stale flag).
  //     Yellow = upcoming; grey = past (info only); red dot = an upcoming result on/before this
  //     position's expiry. A PAST result never gets a red dot. (Mirrors MW computeRow.) ───
  const _resToday = new Date(); _resToday.setHours(0, 0, 0, 0);
  const _nextResult = pos.nextResultDate ? new Date(pos.nextResultDate) : null;
  const isUpcomingResult = !!(_nextResult && _nextResult >= _resToday);
  const resultsBeforeExpiry = isUpcomingResult && _nextResult <= expiryDate;

  // ─── Build the complete row ───
  return {
    // Identity (from backend, passed through)
    id: `${pos.instrument_token}-${pos.account}`, // unique key for React
    tradingsymbol: pos.tradingsymbol,
    instrument_token: pos.instrument_token,
    account: pos.account,
    accountLabel: pos.accountLabel,

    // Col 1-8: Static instrument info
    underlying: pos.underlying,
    buyOrSell: pos.buyOrSell,
    publicPrivate: pos.publicPrivate,
    sector: pos.sector,
    category: pos.category,
    type: pos.type,
    expiryStr: pos.expiryStr,
    expiry: pos.expiry,
    strike: pos.strike,
    symbol: pos.symbol,
    isIndex: pos.isIndex,
    underlyingToken: pos.underlyingToken,

    // Col 9-11: Live prices
    spot,
    avgPrice,
    ltp,

    // Col 12: Percentage
    percentage,

    // Col 13-14: Size
    lotSize,
    lots: absLots,

    // Col 15-16: Margins
    margin,
    totalMargin,

    // Col 17-19: Premium & Profit
    totalPremium,
    remaining,
    profit,

    // Col 20-23: Returns
    returns,
    returnsLeft,
    annualizedReturn,
    annualizedReturnsLeft,

    // Col 24-25: IV
    // entryIV is sourced from trading_journal (weighted avg across all OPEN rows for
    // this account+instrument+direction). Backend overlay in positions-api.js.
    entryIV: pos.entryIV || 0,
    currentIV,
    strikeStockRatio,

    // Col 26-27: Market data
    volume,
    oi,

    // Col 28: Account (already in accountLabel above)

    // Col 29: Result date
    resultDate: pos.resultDate || '',
    resultDateISO: pos.resultDateISO || '',
    isFutureResult: isUpcomingResult,
    nextResultDate: pos.nextResultDate || '',
    resultsBeforeExpiry,

    // Col 30: Trade button (rendered by PositionsTable, no data needed here)

    // Extra: for sorting/filtering
    daysToExpiry,
    isSold,
    quantity: pos.quantity,
    depth,
    bestBuyPrice: bestBuy?.price || 0,
    bestBuyQty: bestBuy?.quantity || 0,
    bestSellPrice: bestSell?.price || 0,
    bestSellQty: bestSell?.quantity || 0,
    product: pos.product,
  };
}
